// Admin: opening hours + check-in/out times shown on the public Contact page.
import React, { useEffect, useState } from 'react';
import { Clock, Save, Loader2, RotateCcw } from 'lucide-react';
import { apiCall } from './_shared';
import SeasonalHours from './SeasonalHours';

const FIELDS = [
  ['check_in', 'Check-in from', 'time'], ['check_out', 'Check-out until', 'time'],
  ['breakfast_from', 'Breakfast from', 'time'], ['breakfast_to', 'Breakfast until', 'time'],
  ['pool_from', 'Pool opens', 'time'], ['pool_to', 'Pool closes', 'time'],
  ['whatsapp_from', 'WhatsApp replies from', 'time'], ['whatsapp_to', 'WhatsApp replies until', 'time'],
  ['reception', 'Reception', 'text'],
];

const HoursEditor = () => {
  const [values, setValues] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    apiCall('/api/admin/hotel-info/hours').then((d) => { setValues(d.hours); setDefaults(d.defaults); }).catch((e) => setMsg(e.message));
  }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, values[k]]));
      await apiCall('/api/admin/hotel-info/hours', { method: 'PUT', body: JSON.stringify(body) });
      setMsg('Saved — the Contact page shows the new hours immediately.');
    } catch (e) { setMsg(e.message); } finally { setSaving(false); }
  };

  if (!values) return <div className="bg-white rounded-xl border p-6 text-sm text-slate-500" data-testid="hours-editor-loading">Loading opening hours…</div>;

  const inputs = FIELDS.map(([k, label, type]) => (
    <label key={k} className="text-sm">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{label}</span>
      <input type={type} value={values[k] || ''} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid={`hours-input-${k}`} />
    </label>
  ));

  return (
    <div id="hours-editor" className="bg-white rounded-xl border shadow-sm p-6 scroll-mt-24" data-testid="hours-editor">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-orange-500" /> Opening hours & check-in / check-out</h3>
        <button type="button" onClick={() => setValues({ ...values, ...defaults })} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="hours-reset">
          <RotateCcw className="w-3 h-3" /> Reset to defaults
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Labels are translated automatically in all 7 languages — you only edit the times. Shown on /contact and used for the check-in / check-out times across the site.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{inputs}</div>
      <div className="mt-5 flex items-center gap-3">
        <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold rounded-lg px-4 py-2 hover:bg-slate-700 disabled:opacity-60" data-testid="hours-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save hours
        </button>
        {msg && <span className="text-sm text-emerald-700" data-testid="hours-msg">{msg}</span>}
      </div>
      <SeasonalHours base={values} />
    </div>
  );
};

export default HoursEditor;
